import { useCallback, useMemo, useState } from 'react';

// Width of the sticky actions track appended when `hasActionsColumn = true`.
const ACTIONS_OVERHEAD = 80;
// Hard floor when no per-key floor is provided. Mirrors the floor inside
// PunchupColumnHeader's drag handler.
const RESIZE_FLOOR_PX = 60;

/**
 * Default track expression for a data column at rest. Each column is at least
 * its `max-content` (header text + widest cell across the body via subgrid)
 * and absorbs an equal share of any free width via `1fr`.
 *
 * Combined with `.tableContainer { width: 100% }` and `.tableScroll > table
 * { width: 100%; min-width: max-content }`, this produces a continuous duality
 * the user articulated as: "more columns ⇒ less slack per column ⇒ tighter
 * ⇒ eventually overflow ⇒ scroll."
 *
 *   (a) sum-of-max-content ≤ page width → table at `width: 100%` (page
 *       width). Free space = page − sum, distributed equally across N data
 *       columns as `(page − sum) / N` per column. Every column gets the same
 *       slack share — even, predictable breathing room. As columns are added,
 *       slack per column shrinks; as they are removed, it grows.
 *   (b) sum-of-max-content > page width → `min-width: max-content` forces the
 *       table beyond `width: 100%`; `.tableScroll` shows a horizontal
 *       scrollbar. Every `1fr` resolves to 0 because there is no slack to
 *       distribute; each column sits at exactly its `max-content`. Tight
 *       columns + scroll.
 *
 * The transition between (a) and (b) is continuous — there is no design
 * boundary; the same expression handles both. Two prior regimes were
 * rejected:
 *   - `max-content` everywhere with container `width: max-content` → tight
 *     columns in (b) but the bordered box stopped at the rightmost data
 *     column in (a) instead of filling the page width.
 *   - `max-content` with a single `1fr` spacer track → case (a) absorbed
 *     slack into one phantom column; the user wants slack distributed across
 *     EVERY column (each absorbing some), not concentrated in one.
 *
 * Don't change this without re-reading PunchupTable CLAUDE.md → "Column
 * width strategy".
 */
const DEFAULT_TRACK = 'minmax(max-content, 1fr)';

export interface UseResizableColumnsOptions {
	/** Append a fixed sticky-actions track to the end of `gridColumns` (width = `actionsWidth`, default 80px). */
	hasActionsColumn?: boolean;
	/**
	 * Width (px) of the trailing sticky-actions track. Defaults to 80 (sized for one icon button).
	 * Pass a larger value when the actions cell holds more than one control — e.g. the Attendee List
	 * renders Print + the ⋮ menu, so it passes ~120.
	 */
	actionsWidth?: number;
	/**
	 * Per-key minimum pixel width — used as the floor when the user drags the
	 * column smaller than this value. Keys not present default to a 60px global
	 * floor.
	 */
	minWidthByKey?: Map<string, number>;
}

export interface UseResizableColumnsReturn {
	/**
	 * CSS `grid-template-columns` value for the table:
	 *   - one `minmax(max-content, 1fr)` track per visible column at rest
	 *     (see DEFAULT_TRACK), or
	 *   - a `<userWidth>px` track for any column the user has resized via the
	 *     header drag handle (truncates with ellipsis below natural width), and
	 *   - a trailing `actionsWidth`px track (default 80) when `hasActionsColumn`
	 *     is `true`.
	 */
	gridColumns: string;
	/** Pixel widths for columns the user has explicitly resized. Keys not
	 *  present in this map use `minmax(max-content, 1fr)` — auto-fit to widest
	 *  cell with `1fr` slack distribution when the table fits the page. */
	columnWidths: Map<string, number>;
	/** Set the user-resized pixel width for a single column. Floored by either
	 *  `minWidthByKey.get(key)` or 60px global. */
	setColumnWidth: (key: string, width: number) => void;
}

/**
 * Manages per-column user-resize state for a Punchup table and emits the
 * `grid-template-columns` string the caller passes to `PunchupTableContainer`.
 *
 * This hook is the single source of truth for column-width logic; tables with
 * a column picker (visibility) compose this hook via `useColumnSelection`.
 *
 * @param visibleKeys - Stable identifiers for the currently visible columns
 *   (heading strings, column-def keys, etc.) in left-to-right render order.
 *   Keys are used to address resize state — keep them stable across renders.
 * @param options - `hasActionsColumn` appends an `actionsWidth`px track
 *   (default 80); `minWidthByKey` sets per-column resize floors.
 */
export function useResizableColumns(
	visibleKeys: string[],
	options?: UseResizableColumnsOptions
): UseResizableColumnsReturn {
	const hasActionsColumn = options?.hasActionsColumn ?? false;
	const actionsWidth = options?.actionsWidth ?? ACTIONS_OVERHEAD;
	const minWidthByKey = options?.minWidthByKey;

	const [columnWidths, setColumnWidths] = useState<Map<string, number>>(() => new Map());

	const gridColumns = useMemo(() => {
		const cols = visibleKeys.map((key) => {
			const userWidth = columnWidths.get(key);
			return userWidth !== undefined ? `${userWidth}px` : DEFAULT_TRACK;
		});
		return hasActionsColumn ? `${cols.join(' ')} ${actionsWidth}px` : cols.join(' ');
	}, [visibleKeys, columnWidths, hasActionsColumn, actionsWidth]);

	const setColumnWidth = useCallback(
		(key: string, width: number) => {
			const floor = minWidthByKey?.get(key) ?? RESIZE_FLOOR_PX;
			setColumnWidths((prev) => new Map([...prev, [key, Math.max(floor, width)]]));
		},
		[minWidthByKey]
	);

	return { gridColumns, columnWidths, setColumnWidth };
}

/** @internal Exported only for tests — do not use in production code. */
export const _ACTIONS_OVERHEAD = ACTIONS_OVERHEAD;
/** @internal Exported only for tests — do not use in production code. */
export const _DEFAULT_TRACK = DEFAULT_TRACK;
/** @internal Exported only for tests — do not use in production code. */
export const _RESIZE_FLOOR_PX = RESIZE_FLOOR_PX;
